import { useNode, useEditor } from "@craftjs/core";
import React, { useRef, useState } from "react";
import { useResponsive } from "../../hooks/useResponsive";
import {
  getButtonSizeStyles,
  getButtonVariantStyles,
  ButtonSize,
  ButtonVariant,
  defaultColors,
} from "../../config/styleConfig";
import { selectedOutlineSx } from "../../config/settingsStyles";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Typography,
  FormControlLabel,
  Switch,
  Button as MuiButton,
  Tabs,
  Tab,
  Checkbox,
} from "@mui/material";
import { useLanguage, getLocalizedText } from "../../context/LanguageContext";

export interface ButtonProps {
  text: string | Record<string, string>;
  link?: string;
  linkType?: "external" | "internal" | "phone" | "email";
  variant?: "primary" | "secondary" | "outline" | "ghost";
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
  fontFamily?: string;
  backgroundColor?: string;
  textColor?: string;
  borderRadius?: number;
  borderColor?: string;
  borderWidth?: number;
  target?: "_self" | "_blank";
  hideOnDesktop?: boolean;
  hideOnMobile?: boolean;
}

export const Button = ({
  text = "Click Me",
  link = "#",
  linkType = "external",
  variant = "primary",
  size = "md",
  fullWidth = false,
  fontFamily,
  backgroundColor,
  textColor,
  borderRadius = 8,
  borderColor,
  borderWidth,
  target = "_self",
  hideOnDesktop = false,
  hideOnMobile = false,
}: ButtonProps) => {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const { enabled: editorEnabled } = useEditor((state) => state.options);
  const { currentLang } = useLanguage();

  const displayText = getLocalizedText(text, currentLang);

  const containerRef = useRef<HTMLElement>(null);
  const editRef = useRef<HTMLElement>(null);
  const [isEditing, setIsEditing] = useState(false);

  const handleDoubleClick = (e: React.MouseEvent) => {
    if (!editorEnabled) return;
    e.stopPropagation();
    setIsEditing(true);
    setTimeout(() => {
      const el = editRef.current;
      if (el) {
        el.focus();
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }, 0);
  };

  const handleEditBlur = () => {
    setIsEditing(false);
    const newText = editRef.current?.innerText || displayText;
    if (newText !== displayText) {
      setProp((props: ButtonProps) => {
        if (typeof props.text === "object") {
          props.text = { ...props.text, [currentLang]: newText };
        } else {
          props.text = newText;
        }
      });
    }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      (e.target as HTMLElement).blur();
    }
    if (e.key === "Escape") {
      if (editRef.current) editRef.current.innerText = displayText;
      (e.target as HTMLElement).blur();
    }
  };
  const { isMobile } = useResponsive(containerRef as React.RefObject<HTMLElement>);

  // Use shared style config for variant styles
  const getVariantStyles = (): { background: string; color: string; border: string } => {
    const safeVariant = (variant || "primary") as ButtonVariant;
    const styles = getButtonVariantStyles(
      safeVariant,
      `var(--color-primary, ${defaultColors.primary})`,
      `var(--color-secondary, ${defaultColors.secondary})`,
      backgroundColor,
      textColor
    );
    return styles;
  };

  // Use shared style config for size styles
  const getSizeStyles = () => {
    const validSizes: ButtonSize[] = ["sm", "md", "lg"];
    const currentSize: ButtonSize = size && validSizes.includes(size as ButtonSize) ? (size as ButtonSize) : "md";
    const sizeStyles = getButtonSizeStyles(currentSize, isMobile);

    return {
      px: `${sizeStyles.px}px`,
      py: `${sizeStyles.py}px`,
      fontSize: `${sizeStyles.fontSize}px`,
    };
  };

  const getHref = () => {
    switch (linkType) {
      case "phone":
        return `tel:${link.replace(/\s/g, "")}`;
      case "email":
        return `mailto:${link}`;
      case "internal":
        return link.startsWith("/") ? link : `/${link}`;
      default:
        return link;
    }
  };

  const variantStyles = getVariantStyles();
  const sizeStyles = getSizeStyles();

  // Responsive hide
  const shouldHide = (hideOnDesktop && !isMobile) || (hideOnMobile && isMobile);

  // Common button styles with text overflow handling
  const buttonStyles = {
    ...variantStyles,
    ...sizeStyles,
    borderRadius: `${borderRadius}px`,
    ...(fontFamily
      ? { fontFamily: `'${fontFamily}', var(--font-family), sans-serif` }
      : /[\u0E00-\u0E7F]/.test(displayText) ? {} : { fontFamily: "var(--font-family-en, var(--font-family))" }),
    ...(borderColor && borderWidth
      ? { border: `${borderWidth}px solid ${borderColor}` }
      : {}),
    textDecoration: "none",
    display: shouldHide ? "none" : "inline-block",
    cursor: "pointer",
    fontWeight: 500,
    transition: "opacity 0.2s",
    width: fullWidth ? "100%" : "auto",
    textAlign: "center" as const,
    whiteSpace: "nowrap" as const,
    flexShrink: 0,
    boxSizing: "border-box" as const,
    lineHeight: 1.4,
    "&:hover": { opacity: 0.9 },
    ...(selected && selectedOutlineSx),
  };

  if (editorEnabled) {
    return (
      <Box
        component="span"
        ref={(ref: HTMLSpanElement | null) => {
          if (ref) {
            connect(isEditing ? ref : drag(ref));
            (containerRef as React.MutableRefObject<HTMLElement | null>).current = ref;
            (editRef as React.MutableRefObject<HTMLElement | null>).current = ref;
          }
        }}
        onDoubleClick={handleDoubleClick}
        contentEditable={isEditing}
        suppressContentEditableWarning
        onBlur={isEditing ? handleEditBlur : undefined}
        onKeyDown={isEditing ? handleEditKeyDown : undefined}
        sx={{
          ...buttonStyles,
          ...(isEditing && {
            cursor: "text",
            outlineOffset: "2px",
          }),
        }}
      >
        {displayText}
      </Box>
    );
  }

  // Public site - use plain anchor tags (CRA compatible)
  return (
    <Box
      component="a"
      ref={(ref: HTMLAnchorElement | null) => {
        if (ref) {
          connect(drag(ref));
          (containerRef as React.MutableRefObject<HTMLElement | null>).current = ref;
        }
      }}
      href={getHref()}
      target={linkType === "external" ? target : "_self"}
      rel={target === "_blank" ? "noopener noreferrer" : undefined}
      sx={buttonStyles}
    >
      {displayText}
    </Box>
  );
};

const SG = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
    <Box sx={{ px: 1.5, py: 1, bgcolor: "#f9fafb", borderBottom: "1px solid", borderColor: "divider" }}>
      <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>{title}</Typography>
    </Box>
    <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>{children}</Box>
  </Box>
);

const ColorWithReset = ({ label, value, fallback, onChange, onReset }: { label: string; value?: string; fallback: string; onChange: (v: string) => void; onReset: () => void }) => (
  <Box>
    <Typography variant="body2" gutterBottom>{label}</Typography>
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <Box component="input" type="color" value={value || fallback} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        sx={{ width: 48, height: 40, borderRadius: 1, border: "1px solid", borderColor: "divider", cursor: "pointer" }} />
      <MuiButton size="small" onClick={onReset} sx={{ textTransform: "none" }}>Reset</MuiButton>
    </Box>
  </Box>
);

export const ButtonSettings = () => {
  const { currentLang, setCurrentLang } = useLanguage();
  const {
    actions: { setProp },
    text,
    link,
    linkType,
    variant,
    size,
    fullWidth,
    fontFamily,
    backgroundColor,
    textColor,
    borderRadius,
    borderColor,
    borderWidth,
    target,
    hideOnDesktop,
    hideOnMobile,
  } = useNode((node) => ({
    text: node.data.props.text,
    link: node.data.props.link,
    linkType: node.data.props.linkType,
    variant: node.data.props.variant,
    size: node.data.props.size,
    fullWidth: node.data.props.fullWidth,
    fontFamily: node.data.props.fontFamily,
    backgroundColor: node.data.props.backgroundColor,
    textColor: node.data.props.textColor,
    borderRadius: node.data.props.borderRadius,
    borderColor: node.data.props.borderColor,
    borderWidth: node.data.props.borderWidth,
    target: node.data.props.target,
    hideOnDesktop: node.data.props.hideOnDesktop,
    hideOnMobile: node.data.props.hideOnMobile,
  }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Content */}
      <SG title="Content">
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={typeof text === "object"}
              onChange={(e) => {
                setProp((props: ButtonProps) => {
                  if (e.target.checked) {
                    const current = typeof props.text === "string" ? props.text : "";
                    props.text = { th: current, en: "" };
                  } else {
                    const obj = props.text as Record<string, string>;
                    props.text = obj?.th || obj?.en || "";
                  }
                });
              }}
            />
          }
          label={<Typography variant="caption">หลายภาษา (TH/EN)</Typography>}
        />
        {typeof text === "object" ? (
          <>
            <Tabs value={currentLang} onChange={(_, v) => setCurrentLang(v)} sx={{ minHeight: 32, "& .MuiTab-root": { minHeight: 32, py: 0.5, fontSize: "0.75rem" } }}>
              <Tab label="🇹🇭 TH" value="th" />
              <Tab label="🇺🇸 EN" value="en" />
            </Tabs>
            <TextField label={`Button Text (${currentLang.toUpperCase()})`} value={(text as Record<string, string>)[currentLang] || ""} size="small" fullWidth
              onChange={(e) => setProp((props: ButtonProps) => {
                props.text = { ...(props.text as Record<string, string>), [currentLang]: e.target.value };
              })} />
          </>
        ) : (
          <TextField label="Button Text" value={text} size="small" fullWidth
            onChange={(e) => setProp((props: ButtonProps) => (props.text = e.target.value))} />
        )}
        <FormControl size="small" fullWidth>
          <InputLabel>Link Type</InputLabel>
          <Select value={linkType} label="Link Type"
            onChange={(e) => setProp((props: ButtonProps) => (props.linkType = e.target.value as ButtonProps["linkType"]))}
          >
            <MenuItem value="external">External URL</MenuItem>
            <MenuItem value="internal">Internal Page</MenuItem>
            <MenuItem value="phone">Phone Number</MenuItem>
            <MenuItem value="email">Email</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label={linkType === "phone" ? "Phone Number" : linkType === "email" ? "Email Address" : "URL"}
          value={link} size="small" fullWidth
          placeholder={linkType === "phone" ? "02-XXX-XXXX" : linkType === "email" ? "email@example.com" : "https://"}
          onChange={(e) => setProp((props: ButtonProps) => (props.link = e.target.value))} />
        {linkType === "external" && (
          <FormControl size="small" fullWidth>
            <InputLabel>Target</InputLabel>
            <Select value={target} label="Target"
              onChange={(e) => setProp((props: ButtonProps) => (props.target = e.target.value as "_self" | "_blank"))}
            >
              <MenuItem value="_self">Same Window</MenuItem>
              <MenuItem value="_blank">New Window</MenuItem>
            </Select>
          </FormControl>
        )}
      </SG>

      {/* Style */}
      <SG title="Style">
        <FormControl size="small" fullWidth>
          <InputLabel>Font Family</InputLabel>
          <Select
            value={fontFamily || ""}
            label="Font Family"
            onChange={(e) => setProp((props: ButtonProps) => (props.fontFamily = e.target.value || undefined))}
          >
            <MenuItem value="">ตาม Theme</MenuItem>
            <MenuItem value="Sarabun">Sarabun</MenuItem>
            <MenuItem value="Prompt">Prompt</MenuItem>
            <MenuItem value="Kanit">Kanit</MenuItem>
            <MenuItem value="Fahkwang">Fahkwang</MenuItem>
            <MenuItem value="Noto Sans Thai">Noto Sans</MenuItem>
            <MenuItem value="Inter">Inter</MenuItem>
            <MenuItem value="Poppins">Poppins</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Variant</InputLabel>
            <Select value={variant} label="Variant"
              onChange={(e) => setProp((props: ButtonProps) => (props.variant = e.target.value as ButtonProps["variant"]))}
            >
              <MenuItem value="primary">Primary</MenuItem>
              <MenuItem value="secondary">Secondary</MenuItem>
              <MenuItem value="outline">Outline</MenuItem>
              <MenuItem value="ghost">Ghost</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Size</InputLabel>
            <Select value={size} label="Size"
              onChange={(e) => setProp((props: ButtonProps) => (props.size = e.target.value as ButtonProps["size"]))}
            >
              <MenuItem value="sm">Small</MenuItem>
              <MenuItem value="md">Medium</MenuItem>
              <MenuItem value="lg">Large</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <FormControlLabel
          control={<Switch checked={fullWidth} onChange={(e) => setProp((props: ButtonProps) => (props.fullWidth = e.target.checked))} size="small" />}
          label="Full Width"
        />
        <ColorWithReset label="Background Color" value={backgroundColor} fallback="#3b82f6"
          onChange={(v) => setProp((props: ButtonProps) => (props.backgroundColor = v))}
          onReset={() => setProp((props: ButtonProps) => (props.backgroundColor = undefined))} />
        <ColorWithReset label="Text Color" value={textColor} fallback="#ffffff"
          onChange={(v) => setProp((props: ButtonProps) => (props.textColor = v))}
          onReset={() => setProp((props: ButtonProps) => (props.textColor = undefined))} />
      </SG>

      {/* Border */}
      <SG title="Border">
        <Box>
          <Typography variant="body2" gutterBottom>Radius: {borderRadius}px</Typography>
          <Slider value={borderRadius} onChange={(_, v) => setProp((props: ButtonProps) => (props.borderRadius = v as number))} min={0} max={50} size="small" valueLabelDisplay="auto" />
        </Box>
        <Box>
          <Typography variant="body2" gutterBottom>Width: {borderWidth || 0}px</Typography>
          <Slider value={borderWidth || 0} onChange={(_, v) => setProp((props: ButtonProps) => (props.borderWidth = v as number))} min={0} max={10} size="small" valueLabelDisplay="auto" />
        </Box>
        <ColorWithReset label="Border Color" value={borderColor} fallback="#000000"
          onChange={(v) => setProp((props: ButtonProps) => (props.borderColor = v))}
          onReset={() => setProp((props: ButtonProps) => (props.borderColor = undefined))} />
      </SG>

      {/* Visibility */}
      <SG title="Visibility">
        <FormControlLabel
          control={<Switch checked={hideOnDesktop} onChange={(e) => setProp((props: ButtonProps) => (props.hideOnDesktop = e.target.checked))} size="small" />}
          label="Hide on Desktop"
        />
        <FormControlLabel
          control={<Switch checked={hideOnMobile} onChange={(e) => setProp((props: ButtonProps) => (props.hideOnMobile = e.target.checked))} size="small" />}
          label="Hide on Mobile"
        />
      </SG>
    </Box>
  );
};

Button.craft = {
  displayName: "Button",
  props: {
    text: "Click Me",
    link: "#",
    linkType: "external",
    variant: "primary",
    size: "md",
    fullWidth: false,
    fontFamily: undefined,
    backgroundColor: undefined,
    textColor: undefined,
    borderRadius: 8,
    borderColor: undefined,
    borderWidth: undefined,
    target: "_self",
  },
  related: {
    settings: ButtonSettings,
  },
};
